import * as moment from 'moment';
import { ModelBase } from '../../abstract/model-base.model';
import { AccountEntry } from '../../accounting/account-entry.model';
import { AllocationBase } from '../../accounting/abstract/allocation-base.model';
import { Currency } from '../../core/currency.model';
import { Partner } from '../../partner/partner.model';
import { StatusEnum } from 'src/app/@shared/enums/status.enum';
import { tagAccents } from 'src/assets/themes/biifor/components/tag';
import { Shareholder } from '../../client/shareholder.model';
import { BankAccountLineType } from '../config/transaction-type.model';
import { User } from '../../user/user.model';

export abstract class Transaction extends ModelBase {

    static override CLASS_NAME: string = 'Transaction';

    protected _accountEntry!: AccountEntry;
    protected _allocations!: AllocationBase[];
    protected _amount!: number;
    protected _amountOrg!: number;
    protected _amountMatched!: number;
    protected _amountUnmatched!: number;
    protected _amountUnmatchedOrg!: number;
    protected _currency!: Currency;
    protected _customLabel!: string;
    protected _exchangeRate!: number;
    protected _issueFlag!: boolean;
    protected _issueFlagDate!: moment.Moment;
    protected _issueFlagUser!: User;
    protected _partner!: Partner;
    protected _shareholder!: Shareholder;
    protected _transactionDate!: moment.Moment;
    protected _type!: BankAccountLineType;

    constructor(data: any) {
        super(data);
        this._allocations = this._allocations || [];
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes statiques
     ----------------------------------------------------------------------------------------*/

    public static statusSeverity(status: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch (true) {
            case (status === StatusEnum.Justified):
                return 'success';
            case (status === StatusEnum.PartiallyJustified):
                return 'success';
            case (status === StatusEnum.PendingJustification):
                return undefined;
            case (status === StatusEnum.Unprocessed):
                return 'warn';
            default:
                return undefined;
        }
    }

    public static statusLabel(status: string): string {
        switch (true) {
            case (status === StatusEnum.Justified):
                return 'Justifié';
            case (status === StatusEnum.PartiallyJustified):
                return 'Part. rapproché';
            case (status === StatusEnum.PendingJustification):
                return 'À rapprocher';
            case (status === StatusEnum.Unprocessed):
                return 'À traiter';
            default:
                return '';
        }
    }

    public static tagAccent(status: string): Object | undefined {
        switch (true) {
            case (status === StatusEnum.Justified):
                return tagAccents;
            default:
                return undefined;
        }
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes accesseurs
     ----------------------------------------------------------------------------------------*/

    /**
     * Les statuts sont testés par ordre de priorité
     */
    public get statusSeverity(): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch (true) {
            case (this.hasStatus(StatusEnum.Justified)):
                return Transaction.statusSeverity(StatusEnum.Justified);
            case (this.hasStatus(StatusEnum.PartiallyJustified)):
                return Transaction.statusSeverity(StatusEnum.PartiallyJustified);
            case (this.hasStatus(StatusEnum.PendingJustification)):
                return Transaction.statusSeverity(StatusEnum.PendingJustification);
            case (this.hasStatus(StatusEnum.Unprocessed)):
                return Transaction.statusSeverity(StatusEnum.Unprocessed);
            default:
                return Transaction.statusSeverity('');
        }
    }

    /**
     * Les statuts sont testés par ordre de priorité
     */
    public get statusLabel(): string {
        switch (true) {
            case (this.hasStatus(StatusEnum.Justified)):
                return Transaction.statusLabel(StatusEnum.Justified);
            case (this.hasStatus(StatusEnum.PartiallyJustified)):
                return Transaction.statusLabel(StatusEnum.PartiallyJustified);
            case (this.hasStatus(StatusEnum.PendingJustification)):
                return Transaction.statusLabel(StatusEnum.PendingJustification);
            case (this.hasStatus(StatusEnum.Unjustified)):
                return Transaction.statusLabel(StatusEnum.Unjustified);
            case (this.hasStatus(StatusEnum.Unprocessed)):
                return Transaction.statusLabel(StatusEnum.Unprocessed);
            default:
                return '';
        }
    }

    public abstract get allocations(): AllocationBase[];

    public abstract set allocations(value: any);

    public get accountEntry(): AccountEntry {
        return this._accountEntry;
    }

    public set accountEntry(value: AccountEntry) {
        this._setValue('_accountEntry', value, 'AccountEntry');
    }

    public get amount(): number {
        return this._amount;
    }

    public set amount(value: any) {
        this._setValue('_amount', value, 'float');
    }

    public get amountMatched(): number {
        return this._amountMatched;
    }

    public get amountOrg(): number {
        return this._amountOrg;
    }

    public set amountOrg(value: any) {
        this._setValue('_amountOrg', value, 'float');
    }

    public set amountMatched(value: any) {
        this._setValue('_amountMatched', value, 'float');
    }

    public get amountUnmatched(): number {
        return this._amountUnmatched;
    }

    public set amountUnmatched(value: any) {
        this._setValue('_amountUnmatched', value, 'float');
    }

    public get amountUnmatchedOrg(): number {
        return this._amountUnmatchedOrg;
    }

    public set amountUnmatchedOrg(value: any) {
        this._setValue('_amountUnmatchedOrg', value, 'float');
    }

    public get currency(): Currency {
        return this._currency;
    }

    public set currency(value: any) {
        this._setValue('_currency', value, 'Currency');
    }

    public get customLabel(): string {
        return this._customLabel;
    }

    public set customLabel(value: any) {
        this._setValue('_customLabel', value, 'string');
    }

    public get exchangeRate(): number {
        return this._exchangeRate;
    }

    public set exchangeRate(value: any) {
        this._setValue('_exchangeRate', value, 'float', { precision: 5 });
    }

    public get issueFlag(): boolean {
        return this._issueFlag;
    }

    public set issueFlag(value: any) {
        this._setValue('_issueFlag', value, 'boolean');
    }

    public get issueFlagDate(): moment.Moment {
        return this._issueFlagDate;
    }

    public set issueFlagDate(value: any) {
        this._setValue('_issueFlagDate', value, 'datetime');
    }

    public get issueFlagUser(): User {
        return this._issueFlagUser;
    }

    public set issueFlagUser(value: any) {
        this._setValue('_issueFlagUser', value, 'User');
    }

    /**
     * Infobulle du drapeau « Problème » : date de signalement, auteur, puis la note
     * interne (`comment`) si elle existe. HTML (à utiliser avec `[escape]="false"`).
     */
    public get issueFlagTooltip(): string {
        const date = this._issueFlagDate ? this._issueFlagDate.format('DD/MM/YYYY') : '';
        let tooltip = `Problème signalé${date ? ' le ' + date : ''}`;
        if (this._issueFlagUser?.fullName) {
            tooltip += `<br>par ${this._issueFlagUser.fullName}`;
        }
        const comment = (this as any).comment;
        if (comment?.trim()) {
            tooltip += `<br><em>${comment}</em>`;
        }
        return tooltip;
    }

    public get partner(): Partner {
        return this._partner;
    }

    public set partner(value: any) {
        this._setValue('_partner', value, 'Partner');
    }

    public get shareholder(): Shareholder {
        return this._shareholder;
    }

    public set shareholder(value: any) {
        this._setValue('_shareholder', value, 'Shareholder');
    }

    public get transactionDate(): moment.Moment {
        return this._transactionDate;
    }

    public set transactionDate(value: any) {
        this._setValue('_transactionDate', value, 'date');
    }

    public get type(): BankAccountLineType {
        return this._type;
    }

    public set type(value: any) {
        this._setValue('_type', value, 'BankAccountLineType');
    }

}
